/**
 * Input normalization.
 *
 * The engine boundary always receives a NormalizedInput, never the raw form
 * values. This keeps engine implementations (mock or future V2.9.9) independent
 * of UI wording and localisation of raw form inputs.
 *
 * NOTE: The mapping below is intentionally minimal / structural. It does NOT
 * encode any Life Blueprint calculation logic. Any semantic mapping that the
 * V2.9.9 engine requires (e.g. 时辰 → 时柱) must live inside the V2.9.9 engine
 * itself and be sourced from the validated Golden Master.
 */

const TIME_SLOT_PREFIX = /^([子丑寅卯辰巳午未申酉戌亥])时/;

const GENDER_MAP = {
  男: "male",
  女: "female",
};

// Structural region codes only. Real geolocation semantics belong to V2.9.9.
const REGION_MAP = {
  华东地区: "CN-EAST",
  华北地区: "CN-NORTH",
  华南地区: "CN-SOUTH",
  华中地区: "CN-CENTRAL",
  西南地区: "CN-SOUTHWEST",
  西北地区: "CN-NORTHWEST",
  东北地区: "CN-NORTHEAST",
  港澳台地区: "CN-HKMOTW",
  海外地区: "OVERSEAS",

  // V3 International Country / Region profile-context values.
  // These are structural/profile normalization only.
  // No V2.9.9 business rule consumes `region`.
  MY: "马来西亚",
  SG: "新加坡",
  CN: "中国大陆",
  HK: "香港特别行政区",
  MO: "澳门特别行政区",
  TW: "台湾",
  OTHER_ASIA: "亚洲其他地区",
  EUROPE: "欧洲",
  NORTH_AMERICA: "北美洲",
  OCEANIA: "大洋洲",
  MIDDLE_EAST: "中东",
  OTHER: "其他",
};

/**
 * Deterministic djb2-style hash of the canonical string.
 * Used to give every result a stable id.
 */
export function stableHash(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i += 1) {
    h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  }
  return `h${(h >>> 0).toString(36)}`;
}

/**
 * Normalize the raw form input into the shape consumed by engines.
 * @param {import('./schema').RawCustomerInput} raw
 * @returns {import('./schema').NormalizedInput}
 */
export function normalizeInput(raw) {
  const name = (raw?.name || "").trim();
  const birthDateISO = (raw?.birthDate || "").trim();

  const rawTime = (raw?.birthTime || "").trim();
  const match = rawTime.match(TIME_SLOT_PREFIX);
  const birthTimeSlot = match ? match[1] : null;

  // V1 Task 3A Remediation · optional exact HH:MM time collected by
  // the V2.9.9 production input path. Not consumed by the mock
  // engine (structural pass-through only). M01 preprocessing reads
  // this via `birthTimeRaw` when the caller supplies it.
  const birthTimeExactRaw =
    typeof raw?.birthTimeExact === "string" ? raw.birthTimeExact.trim() : "";
  const birthTimeExact = birthTimeExactRaw === "" ? null : birthTimeExactRaw;

  const gender = GENDER_MAP[(raw?.gender || "").trim()] || "unspecified";
  const region = REGION_MAP[(raw?.region || "").trim()] || "UNKNOWN";

  const rawKey = [name, birthDateISO, birthTimeSlot || "?", gender, region]
    .join("|")
    .toLowerCase();

  // V1 Task 1 · Optional pass-through of the caller-supplied audited
  // Golden-Master upstream cells consumed by the V2.9.9 production core
  // adapter. This keeps `normalize.js` byte-compatible for existing
  // callers (mock path never inspects `audited`) while allowing
  // production callers to feed the validated slice. See
  // `engine/v299/productionCoreAdapter.js` for the required shape.
  const audited =
    raw && typeof raw.audited === "object" && raw.audited !== null
      ? raw.audited
      : null;

  const base = {
    name,
    birthDateISO,
    birthTimeSlot,
    birthTimeExact,
    gender,
    region,
    rawKey,
  };
  return audited === null ? base : { ...base, audited };
}
